// IE Hub - Free Select Field Component
// Autocomplete with searchable list and free text entry capability

import React, { forwardRef, useState, useMemo, useEffect } from 'react';
import {
  Autocomplete,
  TextField as MuiTextField,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  alpha,
  styled
} from '@mui/material';
import type { AutocompleteInputChangeReason } from '@mui/material/useAutocomplete';
import ConditionalContainer from '../layout/ConditionalContainer';
import { httpGet } from '@/shared/api/http';
import { customerEndpoints } from '@/shared/api/endpoints';
import type { DigitalLender } from '@/shared/api/types';

interface FreeSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface ValidationRules {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
  patternFlags?: string;
  requiredMessage?: string;
}

interface FreeSelectProps {
  label?: string;
  options: FreeSelectOption[];
  value?: string | number | null;
  onChange?: (value: string | number | null) => void;
  error?: boolean;
  helperText?: string | undefined;
  validation?: ValidationRules;
  onValidate?: (isValid: boolean, error: string) => void;
  placeholder?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  name?: string;
  // When true, suppress local validation and rely on parent (RHF) errors on submit
  deferValidation?: boolean;
  // Custom UI blocks
  helperTop?: string | undefined;
  helperAttached?: string | undefined;
  helperBottom?: string | undefined;
  showErrorBelow?: boolean;
  // Maximum number of options to show in dropdown (default: 10)
  maxOptions?: number;
  // ConditionalContainer integration
  title?: string;
  description?: string;
  containerVariant?: 'paper' | 'outlined' | 'plain';
  showErrorInContainer?: boolean;
  // Remote loading (Freepost lenders)
  loadFreepostLenders?: boolean;
  // Session-replay privacy masking - when true, adds data-private (LogRocket) and data-clarity-mask (Clarity) to mask PII in recordings
  // Defaults to true for security; set to false for non-PII fields
  dataPrivate?: boolean;
}

// Styled components
const Wrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const AttachedBlock = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
  border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
  color: theme.palette.text.secondary,
  fontSize: '0.875rem'
}));

const StyledAutocomplete = styled(Autocomplete<string | number, false, false, true>)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.2s ease-in-out',
    backgroundColor: theme.palette.background.paper,

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
      borderWidth: '1px',
    },

    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.02),
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },

    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },

    '&.Mui-disabled': {
      backgroundColor: alpha(theme.palette.action.disabled, 0.05),
      cursor: 'not-allowed',
    },
  },
}));

const FreeSelect = forwardRef<HTMLDivElement, FreeSelectProps>(({
  label: fieldLabel,
  options,
  value = null,
  onChange,
  error = false,
  helperText: _helperText,
  validation = {},
  onValidate,
  placeholder = 'Type to search or enter custom value',
  fullWidth = true,
  size = 'medium',
  disabled = false,
  name,
  deferValidation = false,
  helperTop,
  helperAttached,
  helperBottom,
  maxOptions,
  title,
  description,
  containerVariant = 'plain',
  showErrorInContainer = false,
  loadFreepostLenders = false,
  // LogRocket privacy masking - defaults to true
  dataPrivate = true,
}, ref) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [validationError, setValidationError] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [resolvedOptions, setResolvedOptions] = useState<FreeSelectOption[]>(options);

  // Keep resolvedOptions in sync when local options change and no remote loading
  useEffect(() => {
    if (!loadFreepostLenders) {
      setResolvedOptions(options);
    }
  }, [options, loadFreepostLenders]);

  // Remote loading for Freepost lenders (company list)
  useEffect(() => {
    if (!loadFreepostLenders) {
      return;
    }

    let cancelled = false;

    const fetchFreepostLenders = async () => {
      try {
        const response = await httpGet<DigitalLender[]>(
          customerEndpoints.listFreepostLenders
        );
        const data = Array.isArray(response?.data) ? response.data : [];
        const seen = new Set<string>();
        const remoteOptions: FreeSelectOption[] = [];

        data.forEach((item) => {
          const name = (item?.creditor ?? '').trim();
          if (!name) return;
          const key = name.toLowerCase();
          if (seen.has(key)) return;
          seen.add(key);
          remoteOptions.push({
            value: name,
            label: name,
          });
        });

        if (!cancelled) {
          setResolvedOptions(remoteOptions);
        }
      } catch {
        // Network errors are already captured by http.ts (FEMA); fall back to local options
        if (!cancelled) {
          setResolvedOptions(options);
        }
      } finally {
      }
    };

    void fetchFreepostLenders();

    return () => {
      cancelled = true;
    };
  }, [loadFreepostLenders]);

  // Convert value to option label or string for display
  const selectedOption = useMemo(() => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // Check if value exists in options
    const option = resolvedOptions.find(opt => opt.value === value || String(opt.value) === String(value));
    if (option) {
      return option.label;
    }

    // Return as string if it's a custom value
    return String(value);
  }, [value, resolvedOptions]);

  // Sync inputValue when value changes externally (but not when user is typing)
  useEffect(() => {
    if (value !== null && value !== undefined && value !== '') {
      const option = resolvedOptions.find(opt => opt.value === value || String(opt.value) === String(value));
      if (option) {
        setInputValue(option.label);
      } else {
        setInputValue(String(value));
      }
    } else {
      setInputValue('');
    }
  }, [value, resolvedOptions]);

  // Filter options based on input value and limit to maxOptions
  const applyLimit = (list: FreeSelectOption[]): FreeSelectOption[] => {
    if (typeof maxOptions === 'number' && Number.isFinite(maxOptions) && maxOptions > 0) {
      return list.slice(0, maxOptions);
    }
    return list;
  };

  const filteredOptions = useMemo(() => {
    const source = resolvedOptions;

    if (!inputValue) {
      return applyLimit(source);
    }

    const filtered = source.filter(option =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    return applyLimit(filtered);
  }, [resolvedOptions, inputValue, maxOptions]);

  // Validation logic
  const validateInput = (inputValue: string | number | null) => {
    let errorMsg = '';
    const stringValue = inputValue === null ? '' : String(inputValue);

    if (validation.required) {
      if (!inputValue || stringValue.trim() === '') {
        errorMsg = validation.requiredMessage || 'This field is required';
      }
    }

    if (stringValue && validation.minLength && stringValue.length < validation.minLength) {
      errorMsg = `Minimum length is ${validation.minLength} characters`;
    }

    if (stringValue && validation.maxLength && stringValue.length > validation.maxLength) {
      errorMsg = `Maximum length is ${validation.maxLength} characters`;
    }

    if (stringValue && validation.pattern) {
      const flags = validation.patternFlags || 'i';
      const regex = new RegExp(validation.pattern, flags);
      if (!regex.test(stringValue)) {
        errorMsg = validation.patternMessage || 'Invalid format';
      }
    }

    if (!deferValidation) {
      setValidationError(errorMsg);

      if (onValidate) {
        onValidate(errorMsg === '', errorMsg);
      }
    }

    return errorMsg === '';
  };

  // Handle change
  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: string | number | null
  ) => {
    // If newValue is a label from options, find the corresponding value
    if (newValue && typeof newValue === 'string') {
      const matchingOption = resolvedOptions.find(opt => opt.label === newValue);
      if (matchingOption) {
        if (onChange) {
          onChange(matchingOption.value);
        }
        if (!deferValidation && Object.keys(validation).length > 0) {
          validateInput(matchingOption.value);
        }
        return;
      }
    }

    // Otherwise, use the value as-is (custom text entry)
    if (onChange) {
      onChange(newValue);
    }

    if (!deferValidation && Object.keys(validation).length > 0) {
      validateInput(newValue);
    }
  };

  // Handle input change (for free text entry and filtering)
  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    setInputValue(newInputValue);

    // Only act on direct user input. Ignore Autocomplete-driven resets to avoid loops.
    if (reason !== 'input') {
      return;
    }

    const normalized = newInputValue.trim().toLowerCase();
    const exactMatch = normalized
      ? resolvedOptions.find((opt) => opt.label.trim().toLowerCase() === normalized)
      : undefined;

    // When the typed value exactly matches an option label, promote it to the option value.
    // This prevents the "last character removed" snapback caused by the parent remaining 1 char behind.
    if (exactMatch && newInputValue) {
      onChange?.(exactMatch.value);
      if (!deferValidation && Object.keys(validation).length > 0) {
        validateInput(exactMatch.value);
      }
      return;
    }

    if (!exactMatch && newInputValue) {
      // User is typing custom text - update immediately for freeSolo behavior
      onChange?.(newInputValue);

      if (!deferValidation && Object.keys(validation).length > 0) {
        validateInput(newInputValue);
      }
    }
  };

  const hasError = ((!deferValidation && !!validationError) || error);

  // Content to render inside ConditionalContainer
  const fieldContent = (
    <Wrapper sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Wrapper sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {fieldLabel && !title && (
          <Typography data-testid="ui-fields-field-content-text-action-1" variant="subtitle1" sx={{
            fontWeight: 500, color: 'primary.main', fontSize: isMobile ? '14px' : '16px',
            lineHeight: isMobile ? '20px' : '22px',
          }}>
            {fieldLabel}
            {validation.required && (
              <Box data-testid="ui-fields-field-content-container-*" component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
            )}
          </Typography>
        )}
        {helperTop && (
          <Typography data-testid="ui-fields-field-content-text-action-2" variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '12px' : '14px', lineHeight: isMobile ? '16px' : '20px' }}>
            {helperTop}
          </Typography>
        )}
        {/* Error message above input field */}
        {hasError && (
          <Typography data-testid="ui-fields-field-content-text-action-3"
            variant="body2"
            color="error"
            sx={{
              fontSize: isMobile ? '12px' : '14px', lineHeight: isMobile ? '16px' : '20px',
              fontWeight: 500
            }}
          >
            {(!deferValidation && validationError) || _helperText || 'This field is required'}
          </Typography>
        )}
      </Wrapper>
      <StyledAutocomplete
        ref={ref}
        freeSolo
        options={filteredOptions.map(option => option.label)}
        value={selectedOption}
        onChange={handleChange}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        disabled={disabled}
        fullWidth={fullWidth}
        size={size}
        renderInput={(params) => (
          <MuiTextField data-testid={`ui-fields-field-content-input-${placeholder}`}
            {...(params as any)}
            {...(name && { name })}
            placeholder={placeholder}
            error={hasError}
            size={size}
            inputProps={{
              ...(params as any).inputProps,
              // Session-replay privacy masking - add data-private (LogRocket) and data-clarity-mask (Clarity) for PII fields
              ...(dataPrivate && { 'data-private': true, 'data-clarity-mask': 'true' }),
            }}
            InputProps={{
              ...(params as any).InputProps,
            }}
            sx={{
              marginBottom: 0, // Remove MUI's default 16px margin-bottom
              '& .MuiFormHelperText-root': {
                display: 'none', // Hide default helper text, we handle it ourselves
              },
              '& .MuiOutlinedInput-root': {
                height: '44px'
              },
              '& .MuiInputBase-input': {
                fontSize: isMobile ? '16px' : '18px',
                lineHeight: isMobile ? '22px' : '24px',
              }
            }}
          />
        )}
        ListboxProps={{
          sx: {
            maxHeight: '300px',
          },
        }}
        componentsProps={{
          popper: {
            ...(dataPrivate && { 'data-private': true, 'data-clarity-mask': 'true' }),
          },
        }}
        sx={{
          '&.ie-hub-field-error': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D5391F !important',
              borderWidth: '2px !important',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D5391F !important',
              borderWidth: '2px !important',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D5391F !important',
              borderWidth: '2px !important',
            },
          },
          ...(hasError ? {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D5391F !important',
              borderWidth: '2px !important',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D5391F !important',
              borderWidth: '2px !important',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D5391F !important',
              borderWidth: '2px !important',
            },
          } : {})
        }}
        className={hasError ? 'ie-hub-field-error' : ''}
      />

      {helperAttached && (
        <AttachedBlock>{helperAttached}</AttachedBlock>
      )}
      {helperBottom && (
        <Typography data-testid="ui-fields-field-content-text-action-4" variant="body2" color="text.secondary" sx={{ mt: validationError ? 0.75 : 1, mb: 1.5 }}>
          {helperBottom}
        </Typography>
      )}
    </Wrapper>
  );

  const containerProps: any = {
    ifError: showErrorInContainer ? hasError : false,
    variant: containerVariant,
    sx: {
      ...(hasError && !showErrorInContainer && {
        borderLeft: '4px solid',
        borderLeftColor: 'error.main',
        paddingLeft: 1.5,
        paddingBottom: 0,
      }),
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 1,
      ...(hasError && !showErrorInContainer && {
        paddingTop: 0,
      }),
      '& > div:first-of-type': {
        mb: 0,
      }
    },
  };

  if (title || (fieldLabel && title !== undefined)) {
    containerProps.title = title || fieldLabel;
  }

  if (description) {
    containerProps.description = description;
  }

  return (
    <ConditionalContainer data-testid="ui-fields-free-select-container-action" {...containerProps}>
      {fieldContent}
    </ConditionalContainer>
  );
});

FreeSelect.displayName = 'FreeSelect';

export default FreeSelect;

